import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { ColumnConfig, ColumnExtractor } from '@ddt-web/data-service';
import { InjectQueryParam } from '@ddt-web/generic-utils';
import { DebugQueryParamKey } from '@ddt-web/url';
import { IHeaderAngularComp } from 'ag-grid-angular';
import { IHeaderParams } from 'ag-grid-community';
import * as result from 'lodash/result';

import { AgSortDirection } from '../../../enums';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'ac-header',
	styleUrls: ['./header.component.scss'],
	template: `
		<div class="header-row" [class]="isRightAligned ? 'float-right' : 'float-left'" [dcrKeys]="[nameDcrKey]">
			<span *ngFor="let word of displayName | splitWordsAndRemoveLast" class="word">{{ word }}</span>
			<div [class.padding-left-full]="isRightAligned">
				<span class="word">{{ displayName | lastWord }}</span>
				<span class="color-black" >
					<dz-icon *ngIf="headerIcon" class="header-icon" [type]="headerIcon"></dz-icon>
				</span>
				<info-bubble [dcrKey]="headerTooltipDcr" [stop-event-propagation]="true"></info-bubble>
				<ng-container [ngSwitch]="sortIcon">
					<dz-icon *ngSwitchCase="SortIcon.None" [type]="SortIcon.None"></dz-icon>
					<dz-icon *ngSwitchCase="SortIcon.Ascending" [type]="SortIcon.Ascending"></dz-icon>
					<dz-icon *ngSwitchCase="SortIcon.Descending" [type]="SortIcon.Descending"></dz-icon>
				</ng-container>
			</div>
		</div>

		<ng-container *ngIf="showEditor"><dz-icon *ngIf="headerTooltipDcr" type="lightbulb" [tooltip]="dataPath"></dz-icon></ng-container>
		<ng-template #dataPath><dcr-cell-content [config]="columnConfig"></dcr-cell-content></ng-template>
   `
})
export class HeaderComponent implements IHeaderAngularComp {
	@InjectQueryParam(DebugQueryParamKey.SHOW_EDITOR) public readonly showEditor: boolean = false;

	public headerParams: IHeaderParams;
	public isRightAligned: boolean;
	public displayName: string;
	public nameDcrKey: string;
	public headerTooltipDcr: string;
	public sortMethod: Function;
	public sortDirection: string;
	public sortIcon: string;
	public columnConfig: ColumnConfig<ColumnExtractor>;
	public headerIcon: string;

	public readonly SortIcon = {
		'Ascending': 'sort-up',
		'Descending': 'sort-down',
		'None': 'sort'
	};

	constructor(private contentService: ContentService, private cdr: ChangeDetectorRef) { }

	public agInit(params: IHeaderParams): void {
		this.headerParams = params;
		this.columnConfig = params['columnConfig'];
		this.isRightAligned = params['isRightAligned'];
		this.nameDcrKey = params['headerText']?.key;
		this.headerIcon = params['headerIcon'];
		this.displayName = !this.headerIcon ? (this.contentService.get(this.nameDcrKey, params['headerText']?.defaultText) || params.displayName) : '';
		this.headerTooltipDcr = params['headerTooltip']?.key;
		this.sortDirection = !this.headerParams.column.getSortIndex() ? this.headerParams.column.getSort() : AgSortDirection.NONE;
		this.sortIcon = this.getSortIcon();
		this.headerParams.column.addEventListener('sortChanged', (event) => {
			this.updateColumnSort(event.columns[0].sort);
		});
	}

	@HostListener('click', ['$event'])
	public onHeaderClick(): void {
		if (result(this.headerParams, 'column.getColDef.sortable')) {
			this.sortColumn();
		}
	}

	private sortColumn(): void {
		const newSortDirection = this.sortDirection === AgSortDirection.ASC ? AgSortDirection.DESC : AgSortDirection.ASC;
		this.headerParams.setSort(newSortDirection);

		this.updateColumnSort(newSortDirection);
	}

	private updateColumnSort(sortDirection: AgSortDirection): void {
		this.sortDirection = sortDirection;
		this.sortIcon = this.getSortIcon();
		this.cdr.detectChanges();
	}

	private getSortIcon(): string {
		if (this.headerParams.column.getColDef().sortable) {
			if (this.sortDirection === AgSortDirection.ASC) {
				return this.SortIcon.Ascending;
			} else if (this.sortDirection === AgSortDirection.DESC) {
				return this.SortIcon.Descending;
			} else {
				return this.SortIcon.None;
			}
		}
	}

	public refresh(): boolean {
		return false;
	}
}
